'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetWinnersRow } from '@morfi/backend-sdk-v2/client';
import { useGetWinnersField } from '@morfi/backend-sdk-v2/client';
import { useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { StatusPill } from '@/components/ui/status-pill';
import { ValueChangeArrow } from '@/components/ui/value-change-arrow';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatPercentValue } from '@/lib/format/format-number';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { FEED_CARD_HOVER_BACKGROUND, FEED_CARD_OUTLINE_INTERACTION, FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
import { winnerHueFromMint } from '@/lib/winners/winner-hue';
import { WINNERS_ROW_GRID_CLASS } from './winners-grid';
import { WinnersRowLiveline } from './winners-row-liveline';
export interface WinnersRowProps {
  /** Winning-outcome mint (the list rowKey). */
  mint: string;
  /** Static row snapshot read from the parent's `query.rows` (non-volatile fields). */
  snapshot: GetWinnersRow;
  priorityImage?: boolean;
}

// Per-locale integer formatter — `new Intl.NumberFormat(locale)` rebuilds
// locale tables on every render, so cache one instance per locale here.
const holderCountFormatterCache: Map<string, Intl.NumberFormat> = new Map();
function getHolderCountFormatter(locale: string): Intl.NumberFormat {
  const cached = holderCountFormatterCache.get(locale);
  if (cached !== undefined) return cached;
  const formatter = new Intl.NumberFormat(locale);
  holderCountFormatterCache.set(locale, formatter);
  return formatter;
}

/** Parse a signed-bps string into its percent + display label, using zero for unavailable values. */
function parseDelta(bps: string | null, locale: string): {
  pct: number;
  label: string;
  isZero: boolean;
} {
  const value = Number(bps ?? 0) / 100;
  const pct = Number.isFinite(value) ? value : 0;
  const abs = Math.abs(pct);
  // A value that rounds to 0.0% is "no change" — neutral, no arrow.
  const isZero = Math.round(abs * 10) === 0;
  const label = `${formatPercentValue(abs, locale, isZero ? 1 : 0)}%`;
  return {
    pct,
    label,
    isZero
  };
}

/**
 * Signed basis-point delta cell. `bps` is a signed string (`'250'` = +2.50%);
 * `null` renders a neutral zero. Up/down arrow + success/destructive tone.
 */
function DeltaCell(t0) {
  const $ = _c(26);
  const {
    bps,
    align,
    locale
  } = t0;
  const justify = align === "end" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  let t1;
  if ($[0] !== bps || $[1] !== locale) {
    t1 = parseDelta(bps, locale);
    $[0] = bps;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const delta = t1;
  if (delta.isZero) {
    let t2;
    if ($[3] !== justify) {
      t2 = cn("flex items-center", justify);
      $[3] = justify;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== delta.label) {
      t3 = <span className="text-body text-foreground-tertiary">{delta.label}</span>;
      $[5] = delta.label;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2 || $[8] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  const isUp = delta.pct > 0;
  let t2;
  if ($[10] !== justify) {
    t2 = cn("flex items-center gap-1", justify);
    $[10] = justify;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = isUp ? "up" : "down";
  const t4 = isUp ? "text-data-profit" : "text-data-loss";
  let t5;
  if ($[12] !== t4) {
    t5 = cn("size-3.5", t4);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <ValueChangeArrow direction={t3} className={t5} />;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = isUp ? "text-data-profit" : "text-data-loss";
  let t8;
  if ($[17] !== t7) {
    t8 = cn("text-body", t7);
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== delta.label || $[20] !== t8) {
    t9 = <span className={t8}>{delta.label}</span>;
    $[19] = delta.label;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t2 || $[23] !== t6 || $[24] !== t9) {
    t10 = <div className={t2}>{t6}{t9}</div>;
    $[22] = t2;
    $[23] = t6;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}

/** 24h-change pill for the mobile card header. Tinted by sign; neutral on 0. */
function Change24hBadge(t0) {
  const $ = _c(11);
  const {
    bps,
    locale
  } = t0;
  let t1;
  if ($[0] !== bps || $[1] !== locale) {
    t1 = parseDelta(bps, locale);
    $[0] = bps;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const delta = t1;
  if (delta.isZero) {
    let t2;
    if ($[3] !== delta.label) {
      t2 = <span className="inline-flex w-fit shrink-0 items-center rounded-pill bg-background-tertiary px-2 py-1.5 text-caption text-foreground-secondary">{delta.label}</span>;
      $[3] = delta.label;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const isUp = delta.pct > 0;
  const t2 = isUp ? "success" : "destructive";
  const t3 = isUp ? "up" : "down";
  let t4;
  if ($[5] !== t3) {
    t4 = <ValueChangeArrow direction={t3} className="size-3" />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== delta.label || $[8] !== t2 || $[9] !== t4) {
    t5 = <StatusPill tone={t2} size="sm" className="pl-1.5">{t4}{delta.label}</StatusPill>;
    $[7] = delta.label;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** One responsive value cell: labelled on compact cards, table-aligned on desktop. */
function MetaStat(t0) {
  const $ = _c(13);
  const {
    label,
    children,
    className,
    testId
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left lg:flex-none lg:items-center lg:gap-0 lg:text-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="truncate text-micro text-foreground-tertiary lg:hidden">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== testId) {
    t3 = testId !== undefined ? {
      "data-testid": testId
    } : {};
    $[4] = testId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== t3) {
    t4 = <span className="truncate text-caption font-medium text-foreground-primary lg:text-body lg:font-normal lg:text-foreground-secondary" {...t3}>{children}</span>;
    $[6] = children;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/** The winning-outcome identity cell — thumbnail + bold label + grey market title. */
function OutcomeIdentity(t0) {
  const $ = _c(21);
  const {
    snapshot,
    mint,
    hue,
    priorityImage
  } = t0;
  const t1 = snapshot.outcomeImageUrl !== "" ? snapshot.outcomeImageUrl : null;
  let t2;
  if ($[0] !== hue || $[1] !== mint || $[2] !== snapshot.outcomeImageNsfw || $[3] !== t1) {
    t2 = {
      kind: "winner",
      mint,
      hue,
      imageUrl: t1,
      nsfw: snapshot.outcomeImageNsfw
    };
    $[0] = hue;
    $[1] = mint;
    $[2] = snapshot.outcomeImageNsfw;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== priorityImage || $[6] !== t2) {
    t3 = <OptimizedImage variant="thumbnail" entity={t2} alt="" size="size-12 lg:size-10" rounded="rounded-overlay-item lg:rounded-control" preview={false} priority={priorityImage} />;
    $[5] = priorityImage;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== snapshot.marketType || $[9] !== t3) {
    t4 = <ThumbWithOrigin marketType={snapshot.marketType}>{t3}</ThumbWithOrigin>;
    $[8] = snapshot.marketType;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== snapshot.outcomeLabel) {
    t5 = <span className="truncate text-body-lg font-medium text-foreground-primary lg:text-body lg:font-medium lg:text-foreground-secondary">{snapshot.outcomeLabel}</span>;
    $[11] = snapshot.outcomeLabel;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== snapshot.title) {
    t6 = <span className="line-clamp-1 text-caption text-foreground-tertiary">{snapshot.title}</span>;
    $[13] = snapshot.title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <div className="flex min-w-0 flex-col">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t7) {
    t8 = <div className="flex min-w-0 items-center gap-3">{t4}{t7}</div>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/**
 * One Winners-feed entry. Static fields (title, outcome identity, graduated-at,
 * cover image) ride on the `snapshot` prop; volatile cells (price, volume,
 * market cap, 1h/24h deltas, sparkline, holders) subscribe per-field via
 * `useGetWinnersField(mint, …)` so a NATS AMM patch re-renders only
 * this row, not the whole feed (sdk-list-rendering.md pattern a — the feed is
 * server-sorted, so the parent has no derived state to compute from rows).
 * Renders one responsive tree: a desktop grid row (lg+) and a Launchpad-style
 * stacked card (<lg). CSS repositions the shared image, values, and chart so
 * hidden breakpoints do not mount duplicate work.
 */
export function WinnersRow(t0) {
  const $ = _c(94);
  const {
    mint,
    snapshot,
    priorityImage: t1
  } = t0;
  const priorityImage = t1 === undefined ? false : t1;
  const tTimeIntl = useTranslations("time");
  const tWinners = useTranslations("markets.winners");
  const locale = useLocale();
  let t2;
  if ($[0] !== tTimeIntl) {
    t2 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tTime = t2;
  const {
    formatD6
  } = useFormattedCurrency();
  const prefersReduced = useReducedMotion();
  let t3;
  if ($[2] !== mint) {
    t3 = winnerHueFromMint(mint);
    $[2] = mint;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hue = t3;
  const livePrice = useGetWinnersField(mint, "price");
  const liveVolumeTotal = useGetWinnersField(mint, "volumeTotal");
  const liveMarketCap = useGetWinnersField(mint, "marketCap");
  const liveChange1hBps = useGetWinnersField(mint, "change1hBps");
  const liveChange24hBps = useGetWinnersField(mint, "change24hBps");
  const liveSparkline = useGetWinnersField(mint, "sparkline7d");
  const liveHolderCount = useGetWinnersField(mint, "holderCount");
  const price = livePrice ?? snapshot.price;
  const volumeTotal = liveVolumeTotal ?? snapshot.volumeTotal;
  const marketCap = liveMarketCap ?? snapshot.marketCap;
  const change1hBps = liveChange1hBps ?? snapshot.change1hBps;
  const change24hBps = liveChange24hBps ?? snapshot.change24hBps;
  const sparkline7d = liveSparkline ?? snapshot.sparkline7d;
  const holderCount = liveHolderCount ?? snapshot.holderCount;
  const href = `/winners/${mint}`;
  const intent = useIntentRoutePrefetch(href);
  const t4 = snapshot.graduatedAt ?? "";
  let t5;
  if ($[4] !== t4 || $[5] !== tTime) {
    t5 = formatRelativeTime(t4, tTime, {
      style: "short"
    });
    $[4] = t4;
    $[5] = tTime;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const graduatedLabel = t5;
  let t6;
  if ($[7] !== formatD6) {
    t6 = n => formatD6(String(Math.round(n)), "compact");
    $[7] = formatD6;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const formatMoney = t6;
  let t7;
  if ($[9] !== holderCount || $[10] !== locale) {
    const t8 = getHolderCountFormatter(locale);
    let t9;
    if ($[12] !== holderCount) {
      t9 = Math.round(holderCount);
      $[12] = holderCount;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    t7 = t8.format(Math.max(0, t9));
    $[9] = holderCount;
    $[10] = locale;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const holders = t7;
  const t8 = prefersReduced !== true && FEED_CARD_OUTLINE_INTERACTION;
  let t9;
  if ($[14] !== t8) {
    t9 = cn(WINNERS_ROW_GRID_CLASS, "relative flex flex-col items-stretch rounded-card bg-background-primary px-4 py-4 lg:grid lg:items-center", FEED_CARD_HOVER_BACKGROUND, FEED_CARD_RESTING_OUTLINE, t8);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== href || $[17] !== intent || $[18] !== snapshot.outcomeLabel) {
    t10 = <Link href={href} prefetch={false} {...intent} aria-label={snapshot.outcomeLabel} className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary" />;
    $[16] = href;
    $[17] = intent;
    $[18] = snapshot.outcomeLabel;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== hue || $[21] !== mint || $[22] !== priorityImage || $[23] !== snapshot) {
    t11 = <div className="min-w-0 flex-1 lg:col-start-1 lg:row-start-1"><OutcomeIdentity snapshot={snapshot} mint={mint} hue={hue} priorityImage={priorityImage} /></div>;
    $[20] = hue;
    $[21] = mint;
    $[22] = priorityImage;
    $[23] = snapshot;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== change24hBps || $[26] !== locale) {
    t12 = <div className="shrink-0 lg:hidden"><Change24hBadge bps={change24hBps} locale={locale} /></div>;
    $[25] = change24hBps;
    $[26] = locale;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <div className="flex min-w-0 items-center gap-3 lg:contents">{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== change1hBps || $[32] !== locale) {
    t14 = <div className="hidden lg:col-start-3 lg:row-start-1 lg:block"><DeltaCell bps={change1hBps} align="center" locale={locale} /></div>;
    $[31] = change1hBps;
    $[32] = locale;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== change24hBps || $[35] !== locale) {
    t15 = <div className="hidden lg:col-start-4 lg:row-start-1 lg:block"><DeltaCell bps={change24hBps} align="center" locale={locale} /></div>;
    $[34] = change24hBps;
    $[35] = locale;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== tWinners) {
    t16 = tWinners("marketCap");
    $[37] = tWinners;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16) {
    t17 = <span className="text-micro leading-none text-foreground-tertiary lg:hidden">{t16}</span>;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = Number(marketCap);
  let t19;
  if ($[41] !== formatMoney || $[42] !== t18) {
    t19 = <span className="mt-1 text-heading-lg font-medium leading-none text-foreground-primary lg:mt-0 lg:text-body lg:font-normal lg:leading-normal lg:text-foreground-secondary" data-testid="winners-row-marketcap"><AnimatedNumber value={t18} format={formatMoney} animateWithColour={true} duration={400} /></span>;
    $[41] = formatMoney;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t19) {
    t20 = <div className="flex min-w-0 flex-col gap-0 lg:col-start-7 lg:row-start-1 lg:block lg:text-center">{t17}{t19}</div>;
    $[44] = t17;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== change24hBps || $[48] !== price || $[49] !== sparkline7d) {
    t21 = <div className="h-11 w-32 shrink-0 lg:col-start-8 lg:row-start-1 lg:flex lg:h-9 lg:w-auto lg:items-center lg:justify-end"><div className="h-11 w-32 lg:h-9 lg:w-28"><WinnersRowLiveline series={sparkline7d} currentPriceD9={price} change24hBps={change24hBps} /></div></div>;
    $[47] = change24hBps;
    $[48] = price;
    $[49] = sparkline7d;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t20 || $[52] !== t21) {
    t22 = <div className="flex items-center justify-between gap-3 lg:contents">{t20}{t21}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="h-px w-full bg-border-primary lg:hidden" aria-hidden="true" />;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== tWinners) {
    t24 = tWinners("price");
    $[55] = tWinners;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = Number(price);
  let t26;
  if ($[57] !== formatMoney || $[58] !== t25) {
    t26 = <AnimatedNumber value={t25} format={formatMoney} animateWithColour={true} duration={400} />;
    $[57] = formatMoney;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t24 || $[61] !== t26) {
    t27 = <MetaStat label={t24} className="lg:col-start-2 lg:row-start-1" testId="winners-row-price">{t26}</MetaStat>;
    $[60] = t24;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== tWinners) {
    t28 = tWinners("totalVolume");
    $[63] = tWinners;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== formatD6 || $[66] !== volumeTotal) {
    t29 = formatD6(volumeTotal, "compact");
    $[65] = formatD6;
    $[66] = volumeTotal;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t28 || $[69] !== t29) {
    t30 = <MetaStat label={t28} className="lg:col-start-5 lg:row-start-1">{t29}</MetaStat>;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== tWinners) {
    t31 = tWinners("graduated");
    $[71] = tWinners;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  const t32 = graduatedLabel === "" ? "\u2014" : graduatedLabel;
  let t33;
  if ($[73] !== t31 || $[74] !== t32) {
    t33 = <MetaStat label={t31} className="lg:col-start-6 lg:row-start-1" testId="winners-row-graduated">{t32}</MetaStat>;
    $[73] = t31;
    $[74] = t32;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== tWinners) {
    t34 = tWinners("holders");
    $[76] = tWinners;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== holders || $[79] !== t34) {
    t35 = <MetaStat label={t34} className="lg:hidden">{holders}</MetaStat>;
    $[78] = holders;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] !== t27 || $[82] !== t30 || $[83] !== t33 || $[84] !== t35) {
    t36 = <div className="flex min-w-0 gap-2 lg:contents">{t27}{t30}{t33}{t35}</div>;
    $[81] = t27;
    $[82] = t30;
    $[83] = t33;
    $[84] = t35;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== t10 || $[87] !== t13 || $[88] !== t14 || $[89] !== t15 || $[90] !== t22 || $[91] !== t36 || $[92] !== t9) {
    t37 = <div data-testid="winners-row" className={t9}>{t10}{t13}{t14}{t15}{t22}{t23}{t36}</div>;
    $[86] = t10;
    $[87] = t13;
    $[88] = t14;
    $[89] = t15;
    $[90] = t22;
    $[91] = t36;
    $[92] = t9;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  return t37;
}